/**
 * SE を鳴らす `AudioPort`（索引 Issue #498 の段階 8）。**`duelEditor/timeline/` の外**。
 *
 * **いつ鳴らすかはコアが決める**（`duelEditor/timeline/cues.js` が帯から作ったきっかけの一覧）。ここは鳴らす手段だけ:
 *   再生している間だけ、**前に予約した位置から今 + 先読み分**までのきっかけを AudioContext の時計で予約する。
 *   止めた・シークした（戻った・大きく飛んだ）ときは鳴らさずに位置だけ覚え直す（シークで鳴り散らかさない）。
 *
 * 時刻は `ClockPort` から引く（コアもポートも時計を読まないので、実時間はここに入る）。
 */
import { AudioPort } from '../duelEditor/timeline/ports/audio';
import { cueNames, cuesBetween } from '../duelEditor/timeline/cues';
import { loadSeBuffers } from './seAudio';

/** これより大きく飛んだら「シークした」とみなす */
export const SEEK_JUMP_MS = 400;
/** 少し先のきっかけまで予約する（画面の更新の間隔ぶん遅れないように） */
export const LOOKAHEAD_MS = 120;
const TICK_MS = 50;

/**
 * 予約済みの位置 `until` → 今 `now` で新しく予約するきっかけ。
 * `until` が無い・戻った・大きく飛んだなら null（鳴らさずに覚え直す）。**純関数**
 */
export function cuesToFire(cues, until, now, lookahead = 0) {
  if (until == null || now < until - lookahead - 50 || now - until > SEEK_JUMP_MS) return null;
  return cuesBetween(cues, until, now + lookahead);
}

/**
 * @param urlOf 名前 → URL（共通編集素材の `se/<name>.mp3`）
 * @param clock 今の位置を引く `ClockPort`
 */
export function createWebAudioPort({ urlOf, clock, fetchImpl } = {}) {
  const buffers = new Map();
  let ctx = null;
  let timer = null;
  let until = null;
  let cues = [];
  let volume = 0.8;
  let failed = [];

  const ensureCtx = () => {
    if (ctx) return ctx;
    const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!Ctx) return null;
    ctx = new Ctx();
    return ctx;
  };

  const load = () => {
    if (!ctx) return;
    loadSeBuffers({ ctx, names: cueNames(cues), urlOf, cache: buffers, fetchImpl })
      .then((res) => { failed = res.failed ?? []; })
      .catch(() => {});
  };

  const fire = (list, now) => {
    for (const cue of list) {
      const buf = buffers.get(cue.name);
      if (!buf) continue;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const gain = ctx.createGain();
      gain.gain.value = volume * (cue.gain ?? 1);
      src.connect(gain).connect(ctx.destination);
      src.start(ctx.currentTime + Math.max(0, (cue.ms - now) / 1000));
    }
  };

  const tick = () => {
    const now = clock?.ms?.() ?? 0;
    const list = cuesToFire(cues, until, now, LOOKAHEAD_MS);
    if (list) fire(list, now);
    until = now + LOOKAHEAD_MS;
  };

  const port = AudioPort.create({
    /** 再生を始めるときに呼ぶ（きっかけの一覧と音量を渡す） */
    schedule: (list = [], opts = {}) => {
      cues = list;
      if (Number.isFinite(opts.volume)) volume = opts.volume;
      if (!ensureCtx()) return;
      ctx.resume?.();
      load();
      until = clock?.ms?.() ?? 0;
      if (timer == null) timer = setInterval(tick, TICK_MS);
    },
    stop: () => {
      if (timer != null) clearInterval(timer);
      timer = null;
      until = null;
    },
    status: () => ({ ready: Boolean(ctx), error: failed.length ? `読めなかった SE: ${failed.join(', ')}` : null }),
  });

  return { ...port, dispose: () => { port.stop(); ctx?.close?.(); ctx = null; } };
}
